/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useMemo} from 'react';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import {translate} from '@ion/core/src/locale/locale';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {Entry} from './SSOApps';
import {SSOFormFields} from './SSOEditorUtil';

export enum SSOFieldCharacterLimits {
  CLIENT_ID_MAX_LENGTH = 128,
  CLIENT_SECRET_MAX_LENGTH = 256,
  REDIRECT_URI_MAX_LENGTH = 2048
}

export interface SSOAppEntryProps {
  handleFieldChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChooserChange?: Function;
  onDelete?: Function;
  classes?: ClassNameMap<string>,
  ssoLevel: Entry[];
  item?: Entry;
  minApp?: number;
  disabled?: boolean;
};

/**
 * The SSO Apps Entry Editor provides the UI which allows to add new apps, such as their
 * appName, clientId, clientSecret and redirectUri on new SSO Configuration creation.
 *
 * ```typescript
 *
 *    import {SSOAppEntry} from '@ion/applications/src/pages/admin/accounts/SSOApps';
 *
 *    <SSOAppEntry handleChooserChange={handleChooserChange}
 *      handleFieldChange={handleFieldChange}
 *      onDelete={onDelete}
 *      classes={classes}
 *      ssoLevel={ssoLevel}
 *      item={item}
 *      minApp={minApp} />
 *
 * ```
 */
export const SSOAppEntry = ({
  handleChooserChange,
  handleFieldChange,
  onDelete,
  ssoLevel,
  item,
  classes,
  minApp = 0,
  disabled = false
}: SSOAppEntryProps) => {
  const appOptions = translate('admin-groups:appListOptions', {returnObjects: true});
  const strId      = item.id?.toString();
  const app        = item.apps;
  
  const {
    CLIENT_ID_MAX_LENGTH,
    CLIENT_SECRET_MAX_LENGTH,
    REDIRECT_URI_MAX_LENGTH
  } = SSOFieldCharacterLimits;

  const isOverMaxClientIdLength = app[SSOFormFields.ClientId]?.length > CLIENT_ID_MAX_LENGTH;
  
  const clientIdErrorMessage = useMemo(() => {
    return isOverMaxClientIdLength
      ? translate('admin-groups:ssoConfig.clientId.error')
      : translate('common:editors.shared.required');
  }, [isOverMaxClientIdLength]);

  return (
    <>
      {ssoLevel?.length > minApp && (
        <div className={classes.buttonStyle}>
          <TertiaryButton onClick={onDelete(strId)}
            key={`deleteButton_${strId}`}
            disabled={disabled}
            id={strId}>
            {translate('common:delete')}
          </TertiaryButton>
        </div>
      )}
      <FormItem>
        <Dropdown options={appOptions}
          disabled={disabled}
          fieldLabel={translate('admin-groups:ssoConfig.appList.label')}
          placeholder={translate('admin-groups:ssoConfig.appList.placeholder')}
          hasError={app?.app_id_error}
          errorText={translate('admin-groups:ssoConfig.appList.error')}
          value={app[SSOFormFields.AppId] || ''}
          onChange={function({target: {value}}, child) {
            handleChooserChange({
              name  : SSOFormFields.AppId,
              value : child?.value || value,
              id    : `${SSOFormFields.AppId}_${strId}`
            });
          }}
          required />
      </FormItem>
      <FormItem>
        <FormTextField fieldLabel={translate('admin-groups:ssoConfig.clientId.label')}
          placeholder={translate('admin-groups:ssoConfig.clientId.placeholder')}
          name={SSOFormFields.ClientId}
          key={`${SSOFormFields.ClientId}_${strId}`}
          id={`${SSOFormFields.ClientId}_${strId}`}
          maxLength={CLIENT_ID_MAX_LENGTH}
          onChange={handleFieldChange}
          hasError={isOverMaxClientIdLength || app?.client_id_error}
          errorText={clientIdErrorMessage}
          defaultValue={app[SSOFormFields.ClientId]}
          value={app[SSOFormFields.ClientId]}
          disabled={disabled}
          required />
      </FormItem>
      <FormItem>
        <FormTextField fieldLabel={translate('admin-groups:ssoConfig.clientSecret.label')}
          placeholder={translate('admin-groups:ssoConfig.clientSecret.placeholder')}
          name={SSOFormFields.ClientSecret}
          key={`${SSOFormFields.ClientSecret}_${strId}`}
          id={`${SSOFormFields.ClientSecret}_${strId}`}
          type='password'
          autoComplete='new-password'
          maxLength={CLIENT_SECRET_MAX_LENGTH}
          onChange={handleFieldChange}
          hasError={app?.client_secret_error}
          disabled={disabled}
          errorText={translate('common:editors.shared.required')}
          value={app[SSOFormFields.ClientSecret]} />
      </FormItem>
      <FormItem>
        <FormTextField fieldLabel={translate('admin-groups:ssoConfig.redirectUri.label')}
          placeholder={translate('admin-groups:ssoConfig.redirectUri.placeholder')}
          name={SSOFormFields.RedirectUri}
          key={`${SSOFormFields.RedirectUri}_${strId}`}
          id={`${SSOFormFields.RedirectUri}_${strId}`}
          maxLength={REDIRECT_URI_MAX_LENGTH}
          onChange={handleFieldChange}
          disabled={disabled}
          hasError={app?.redirect_uri_error}
          errorText={translate('admin-groups:ssoConfig.redirectUri.error')}
          value={app[SSOFormFields.RedirectUri]} />
      </FormItem>
    </>
  );
};
export default SSOAppEntry;
